import React, { useState } from 'react';
import { LucideIcon } from 'lucide-react';
import { Card } from '../../atoms/Card';
import { TabButton } from '../../atoms/TabButton';
import { StatValue } from '../../atoms/StatValue';
import { StatDescription } from '../../atoms/StatDescription';

export type TabType = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'total';

export interface TabConfig {
  label: string;
  gradient: string;
  description: string;
}

export interface TabbedStatsConfig {
  daily: TabConfig;
  weekly?: TabConfig;
  monthly: TabConfig;
  yearly?: TabConfig;
  total?: TabConfig;
}

export interface StatsData {
  daily: number;
  weekly?: number;
  monthly: number;
  yearly?: number;
  total?: number;
  totalActive?: number;
  totalPublished?: number;
}

export interface TabbedStatCardProps<T extends StatsData = StatsData> {
  stats: T | null;
  loading?: boolean;
  error?: string | null;
  tabConfig: TabbedStatsConfig;
  getValue: (tab: TabType, stats: T) => number;
  title?: string;
  icon?: LucideIcon;
}

export const TabbedStatCard = <T extends StatsData = StatsData>({
  stats,
  loading = false,
  error = null,
  tabConfig,
  getValue,
  title,
  icon: Icon,
}: TabbedStatCardProps<T>) => {
  const [activeTab, setActiveTab] = useState<TabType>('daily');

  return (
    <Card className="p-0 overflow-hidden">
      {title && (
        <div className="px-0 pt-0 pb-3 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            {Icon && (
              <Icon className="w-4 h-4 text-gray-500" />
            )}
            <h3 className="text-sm font-medium text-gray-700 tracking-wide">
              {title}
            </h3>
          </div>
        </div>
      )}
      {loading ? (
        <div className="text-center text-gray-500 py-8">İstatistikler yükleniyor...</div>
      ) : error ? (
        <div className="text-center text-red-500 py-8">{error}</div>
      ) : stats ? (
        <>
          {/* Gradient Background based on active tab */}
          <div className={`transition-all duration-300 ${tabConfig[activeTab]?.gradient || 'bg-gradient-to-br from-gray-500 to-gray-600'}`}>
            {/* Tabs */}
            <div className="flex border-b border-white/20">
              {(Object.keys(tabConfig).filter(key => tabConfig[key as TabType]) as TabType[]).map((tab) => (
                <TabButton
                  key={tab}
                  label={tabConfig[tab]?.label || ''}
                  isActive={activeTab === tab}
                  onClick={() => setActiveTab(tab)}
                />
              ))}
            </div>

            {/* Tab Content */}
            <div className="text-center py-2 px-4">
              <StatValue value={stats ? getValue(activeTab, stats) : 0} />
              <StatDescription description={tabConfig[activeTab]?.description || ''} />
            </div>
          </div>
        </>
      ) : null}
    </Card>
  );
};

